import { useState, type FormEvent } from "react";
import { Link } from "wouter";
import { api, ApiError, type Item } from "../../api";
import { Thread, type Attachment, type Comment } from "../../components/Thread";
import { EFFORT_LABEL, ErrorNote, formatDate, KindTag, Loading, Moment, PriorityChip, STATUS_LABEL, StatusChip, timeAgo, useApi } from "../../components/ui";
import { usePickers } from "./shared";

interface Detail {
  item: Item;
  comments: Comment[];
  attachments: Attachment[];
  links: Array<{ id: string; number: number; ref: string; kind: string; title: string; status: keyof typeof STATUS_LABEL; githubUrl: string | null }>;
  events: Array<{ actor: string; type: string; data: Record<string, unknown>; createdAt: string }>;
}

export function StaffItem({ number }: { number: string }) {
  const { data, error, loading, reload } = useApi<Detail>(`/items/${number}`);
  const [saveError, setSaveError] = useState<string | null>(null);
  const pickers = usePickers();

  if (loading && !data) return <div className="page"><Loading /></div>;
  if (!data) {
    return (
      <div className="page-narrow">
        <Moment mood="thinking" title="Item not found">
          <p className="muted">{error}</p>
          <Link href="/staff/queue" className="btn">Back to the queue</Link>
        </Moment>
      </div>
    );
  }
  const i = data.item;
  const patch = async (change: Record<string, unknown>) => {
    setSaveError(null);
    try {
      await api(`/items/${i.number}`, { method: "PATCH", json: change });
      reload();
    } catch (e) {
      setSaveError(e instanceof ApiError ? e.message : "Couldn't save.");
    }
  };
  const meta = i.metadata ?? {};
  const context = meta.context as Record<string, unknown> | undefined;

  return (
    <div className="page">
      <p className="eyebrow">
        <Link href="/staff/queue">Queue</Link> / {i.ref}
      </p>
      <div className="row" style={{ alignItems: "flex-start", justifyContent: "space-between", margin: "4px 0 16px" }}>
        <h1 style={{ flex: 1, minWidth: 260 }}>{i.title}</h1>
        <div className="row">
          <PriorityChip priority={i.priority} />
          <StatusChip status={i.status} />
        </div>
      </div>
      <div className="split">
        <div className="stack">
          <section className="card stack">
            <p className="small muted" style={{ margin: 0 }}>
              <KindTag kind={i.kind} /> · {SOURCE_LABEL(i)} ·{" "}
              {i.reporterName || "unknown"}
              {i.reporterEmail && i.reporterEmail !== i.reporterName ? ` <${i.reporterEmail}>` : ""} · {formatDate(i.createdAt)}
            </p>
            {i.body ? <div className="prewrap">{i.body}</div> : <p className="muted">No description.</p>}
            {i.githubUrl && (
              <p style={{ margin: 0 }}>
                <a href={i.githubUrl} target="_blank" rel="noreferrer">
                  Open {i.githubRepo}#{i.githubNumber} on GitHub ↗
                </a>
              </p>
            )}
            {i.labels && i.labels.length > 0 && (
              <div className="row" style={{ gap: 6 }}>
                {i.labels.map((l) => (
                  <span key={l} className="chip chip-plain">{l}</span>
                ))}
              </div>
            )}
            {(meta.pageUrl || (context && Object.keys(context).length > 0)) && (
              <details>
                <summary className="small" style={{ cursor: "pointer", fontWeight: 600 }}>App context (staff only)</summary>
                <pre className="keybox" style={{ whiteSpace: "pre-wrap", marginTop: 8 }}>{JSON.stringify({ pageUrl: meta.pageUrl, ...context }, null, 2)}</pre>
              </details>
            )}
          </section>
          {i.kind === "github_issue" ? (
            <p className="small muted">Discussion for GitHub issues happens on GitHub. Internal notes below stay in the portal.</p>
          ) : null}
          <Thread
            itemKey={i.number}
            comments={data.comments}
            attachments={data.attachments}
            staff
            onChange={reload}
            replyHint={i.kind === "ticket" && i.reporterEmail ? `Replies are emailed to ${i.reporterEmail}. Internal notes are never shown to the customer.` : undefined}
          />
        </div>

        <aside className="stack">
          {i.needsSorting && <SortPanel item={i} customers={pickers.orgs} onDone={reload} />}
          <section className="card stack" data-tour="triage-panel">
            <h2>Triage</h2>
            <ErrorNote message={saveError} />
            <Field label="Status" id="st">
              <select id="st" value={i.status} onChange={(e) => patch({ status: e.target.value })}>
                {Object.entries(STATUS_LABEL).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </Field>
            <Field label="Priority" id="pr">
              <select id="pr" value={i.priority ?? ""} onChange={(e) => patch({ priority: e.target.value === "" ? null : Number(e.target.value) })}>
                <option value="">Not triaged</option>
                <option value="0">P0 Urgent</option>
                <option value="1">P1 High</option>
                <option value="2">P2 Normal</option>
                <option value="3">P3 Low</option>
              </select>
            </Field>
            <Field label="Effort" id="ef">
              <select id="ef" value={i.effort ?? ""} onChange={(e) => patch({ effort: e.target.value === "" ? null : Number(e.target.value) })}>
                <option value="">Not estimated</option>
                {[1, 2, 3, 4, 5].map((n) => (
                  <option key={n} value={n}>{n} · {EFFORT_LABEL[n]}</option>
                ))}
              </select>
            </Field>
            <Field label="Assignee" id="as">
              <select id="as" value={i.assigneeId ?? ""} onChange={(e) => patch({ assigneeId: e.target.value || null })}>
                <option value="">Unassigned</option>
                {pickers.users.map((u) => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Customer" id="og">
              <select id="og" value={i.orgId ?? ""} onChange={(e) => patch({ orgId: e.target.value || null })}>
                <option value="">Internal / none</option>
                {pickers.orgs.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Project" id="pj">
              <select id="pj" value={i.projectId ?? ""} onChange={(e) => patch({ projectId: e.target.value || null })}>
                <option value="">None</option>
                {pickers.projects.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Due" id="du">
              <input id="du" type="date" value={i.dueAt ? i.dueAt.slice(0, 10) : ""} onChange={(e) => patch({ dueAt: e.target.value ? new Date(`${e.target.value}T23:59:00`).toISOString() : null })} />
            </Field>
          </section>

          {i.scoreExplained && (
            <section className="card card-tight" data-tour="score-card">
              <h3>Queue score: {i.scoreExplained.score}</h3>
              <ul className="small muted" style={{ paddingLeft: 18, margin: "8px 0 0" }}>
                {i.scoreExplained.reasons.map((r) => (
                  <li key={r}>{r}</li>
                ))}
              </ul>
            </section>
          )}

          <Links item={i} links={data.links} onChange={reload} />
          {i.kind !== "github_issue" && <CreateIssue item={i} onDone={reload} />}

          <section className="card card-tight">
            <h3>Activity</h3>
            <ul className="list-plain small">
              {data.events.slice(0, 15).map((e, idx) => (
                <li key={idx}>
                  <strong>{e.actor}</strong> {describeEvent(e)} <span className="muted">· {timeAgo(e.createdAt)}</span>
                </li>
              ))}
            </ul>
          </section>
        </aside>
      </div>
    </div>
  );
}

function SOURCE_LABEL(i: Item): string {
  switch (i.source) {
    case "in_app":
      return `In-app from ${i.appName ?? "an app"}`;
    case "github":
      return "Mirrored from GitHub";
    case "portal":
      return "Submitted in the portal";
    case "email":
      return "Emailed to support";
    case "api":
      return `Sent through the API${typeof i.metadata?.channel === "string" ? ` (${i.metadata.channel})` : ""}`;
    default:
      return "Created by staff";
  }
}

function Field({ label, id, children }: { label: string; id: string; children: React.ReactNode }) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {children}
    </div>
  );
}

function describeEvent(e: Detail["events"][number]): string {
  const d = e.data as { from?: unknown; to?: unknown; ref?: string; name?: string; url?: string; source?: string };
  switch (e.type) {
    case "created":
      return `created this (${d.source ?? ""})`;
    case "status":
      return `changed status ${String(d.from)} → ${String(d.to)}`;
    case "priority":
      return `set priority ${d.to === null ? "untriaged" : `P${String(d.to)}`}`;
    case "linked":
      return `linked ${d.ref}`;
    case "attachment":
      return `attached ${d.name}`;
    case "github_issue":
      return `opened GitHub issue ${d.ref ?? ""}`;
    default:
      return `changed ${e.type}`;
  }
}

function Links({ item, links, onChange }: { item: Item; links: Detail["links"]; onChange: () => void }) {
  const [target, setTarget] = useState("");
  const [error, setError] = useState<string | null>(null);
  const add = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await api(`/items/${item.number}/links`, { method: "POST", json: { target: target.trim() } });
      setTarget("");
      onChange();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't link.");
    }
  };
  return (
    <section className="card card-tight stack">
      <h3>Linked items</h3>
      {links.length > 0 && (
        <ul className="list-plain small">
          {links.map((l) => (
            <li key={l.id} className="row" style={{ justifyContent: "space-between" }}>
              <span>
                <Link href={`/staff/items/${l.number}`} className="mono">{l.ref}</Link> {l.title}
              </span>
              <span className="row" style={{ gap: 6 }}>
                <StatusChip status={l.status} />
                <button className="btn-link small" aria-label={`Unlink ${l.ref}`} onClick={() => api(`/items/${item.number}/links/${l.id}`, { method: "DELETE" }).then(onChange)}>
                  Unlink
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
      <form className="row" onSubmit={add}>
        <label htmlFor="link-target" className="sr-only">Item to link</label>
        <input id="link-target" type="text" placeholder="DL-1234" value={target} onChange={(e) => setTarget(e.target.value)} style={{ flex: 1, minWidth: 0 }} />
        <button className="btn btn-small" disabled={!target.trim()}>Link</button>
      </form>
      {error && <p className="error-text" role="alert">{error}</p>}
    </section>
  );
}

function CreateIssue({ item, onDone }: { item: Item; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const gh = useApi<{ configured: boolean; repos: string[]; error: string | null }>(open ? "/staff/github" : null);
  const [repo, setRepo] = useState("");
  const [title, setTitle] = useState(item.title);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ url: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ url: string }>(`/staff/items/${item.number}/github-issue`, { method: "POST", json: { repo, title } });
      setResult(r);
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't create the issue.");
    } finally {
      setBusy(false);
    }
  };
  if (!open) {
    return (
      <button className="btn" onClick={() => setOpen(true)} data-tour="create-issue">
        Create GitHub issue
      </button>
    );
  }
  return (
    <section className="card card-tight stack" data-tour="create-issue">
      <h3>Create GitHub issue</h3>
      {result ? (
        <p className="small">
          Created: <a href={result.url} target="_blank" rel="noreferrer">{result.url}</a>
        </p>
      ) : gh.loading ? (
        <Loading />
      ) : !gh.data?.configured ? (
        <p className="small muted">The GitHub App isn't connected yet. See Settings.</p>
      ) : (
        <form className="stack" onSubmit={submit}>
          <ErrorNote message={gh.data.error} />
          <Field label="Repository" id="gh-repo">
            <select id="gh-repo" required value={repo} onChange={(e) => setRepo(e.target.value)}>
              <option value="">Choose…</option>
              {gh.data.repos.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </Field>
          <Field label="Issue title" id="gh-title">
            <input id="gh-title" type="text" required maxLength={256} value={title} onChange={(e) => setTitle(e.target.value)} />
          </Field>
          <p className="small muted" style={{ margin: 0 }}>The description and a link back to {item.ref} are copied over; the new issue is linked here.</p>
          {error && <p className="error-text" role="alert">{error}</p>}
          <button className="btn btn-primary" disabled={busy || !repo}>{busy ? "Creating…" : "Create issue"}</button>
        </form>
      )}
    </section>
  );
}

/** Unsorted items: pick the customer (or internal), optionally teach the matcher about the sender. */
function SortPanel({ item, customers, onDone }: { item: Item; customers: Array<{ id: string; name: string }>; onDone: () => void }) {
  const [customerId, setCustomerId] = useState("");
  const [addContact, setAddContact] = useState(true);
  const [addDomain, setAddDomain] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState<string[]>([]);
  const domain = item.reporterEmail?.split("@")[1];
  const internal = customerId === "internal";
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ notes: string[] }>(`/items/${item.number}/sort`, {
        method: "POST",
        json: { customerId: internal ? null : customerId, addContact: !internal && addContact, addDomain: !internal && addDomain },
      });
      setNotes(r.notes);
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't sort it.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="card stack" onSubmit={submit} data-tour="sort-panel" style={{ borderColor: "var(--amber)" }}>
      <div>
        <h2>Unsorted</h2>
        <p className="small muted" style={{ margin: "6px 0 0" }}>
          {item.reporterEmail ? `${item.reporterEmail} didn't match a customer.` : "This request didn't match a customer."} Pick who it's for.
        </p>
      </div>
      <Field label="Customer" id="sort-customer">
        <select id="sort-customer" required value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
          <option value="">Choose…</option>
          {customers.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
          <option value="internal">Not a customer (internal)</option>
        </select>
      </Field>
      {customerId && !internal && item.reporterEmail && (
        <div className="stack" style={{ gap: 0 }}>
          <label className="check">
            <input type="checkbox" checked={addContact} onChange={(e) => setAddContact(e.target.checked)} /> Add {item.reporterEmail} as a contact
          </label>
          {domain && (
            <label className="check">
              <input type="checkbox" checked={addDomain} onChange={(e) => setAddDomain(e.target.checked)} /> Match everyone at {domain} to this customer
            </label>
          )}
        </div>
      )}
      {error && <p className="error-text" role="alert">{error}</p>}
      {notes.length > 0 && <p className="small muted" role="status">{notes.join(" ")}</p>}
      <button className="btn btn-primary" disabled={busy || !customerId}>
        {busy ? "Sorting…" : "Sort"}
      </button>
    </form>
  );
}
